import { useState } from 'react';
import { ErrorCode, FileRejection, useDropzone } from 'react-dropzone';
import { BUTTON_SIZES } from 'ui/button';
import { Close16 } from 'ui/icons/action/Close16';
import { ErrorFill16 } from 'ui/icons/aurora/16px/ErrorFill16';
import { UploadCircle } from 'ui/icons/action/UploadCircle';
import { colors } from 'ui/theme/colors';
import {
  AcceptedFileContainer,
  Container,
  ErrorMessageWrapper,
  FileAcceptancePrompt,
  Prompt,
  StyledGhostButton,
  StyledSecondaryButton,
} from './FileDropzone.styled';
import { FileDropzoneProps, FileRejectErrorContent } from './types';
import { formulateAcceptedFileTypes } from './utils';

const REJECTION_ERROR_MESSAGE_CONTENT = [
  { code: ErrorCode.FileInvalidType, message: 'Invalid file type' },
  { code: ErrorCode.FileTooLarge, message: 'File is too large' },
  { code: ErrorCode.FileTooSmall, message: 'File is too small' },
  { code: ErrorCode.TooManyFiles, message: 'Too many files' },
];

const FileDropPrompt = ({ error }: { error?: FileRejectErrorContent }) => (
  <Prompt data-test="file-drop-prompt">
    {error ? (
      <ErrorMessageWrapper data-test="reject-error-message">
        <ErrorFill16 color={colors.iconDestructive} />
        {error.message}
      </ErrorMessageWrapper>
    ) : (
      'Drop file here to upload or'
    )}
  </Prompt>
);

const AcceptedFiles = ({
  files,
  onRemove,
}: {
  files: File[];
  onRemove: (fileName: string) => void;
}) =>
  files.map(({ name }, index) => (
    <AcceptedFileContainer key={index} data-test={`dropzone-accepted-file-${index}`}>
      <span>{name}</span>
      <StyledGhostButton
        data-test="remove-file-button"
        size={BUTTON_SIZES.SMALL}
        onClick={() => onRemove(name)}
      >
        <Close16 />
      </StyledGhostButton>
    </AcceptedFileContainer>
  ));

export const FileDropzone = ({
  accept,
  maxFiles,
  multiple = false,
  onCancel,
  onUpload,
  ...props
}: FileDropzoneProps) => {
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<FileRejectErrorContent>();

  const onDrop = (acceptedFiles: File[], fileRejections?: FileRejection[]) => {
    onUpload(acceptedFiles, fileRejections);
    setFiles(acceptedFiles);

    // Handle single file upload error state
    if (fileRejections?.length) {
      if (!multiple) {
        const rejectError = fileRejections[0].errors[0];
        const errorContent = REJECTION_ERROR_MESSAGE_CONTENT.find(
          (content) => content.code === rejectError.code,
        );
        if (errorContent) setError(errorContent);
      }
    } else setError(undefined);
  };

  const onFileDialogCancel = () => {
    onCancel?.();
    setError(undefined);
  };

  const onRemoveFile = (fileName: File['name']) => {
    const newFiles = files.filter((file: File) => file.name !== fileName);
    onDrop(newFiles);
  };

  const {
    fileRejections,
    getInputProps,
    getRootProps,
    isDragAccept,
    isDragReject,
    isFocused,
    open,
  } = useDropzone({
    accept,
    multiple,
    maxFiles,
    onDrop,
    onFileDialogCancel,
    noClick: true,
    noKeyboard: true,
  });

  return (
    <Container
      data-has-file-rejections={!!fileRejections.length}
      {...getRootProps({ isFocused, isDragAccept, isDragReject })}
      {...props}
    >
      <input {...getInputProps()} data-test="file-upload-input" />
      <FileDropPrompt error={error} />
      <StyledSecondaryButton type="button" data-test="open-file-dialog-button" onClick={open}>
        <UploadCircle width={20} />
        Browse files
      </StyledSecondaryButton>
      {files.length ? (
        <AcceptedFiles files={files} onRemove={onRemoveFile} />
      ) : (
        <FileAcceptancePrompt data-test="file-acceptance-prompt">
          Accepts {formulateAcceptedFileTypes(accept)}
        </FileAcceptancePrompt>
      )}
    </Container>
  );
};
